import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, AlertTriangle, CheckCircle, Clock, XCircle, RefreshCw, Loader2, Play, UserX, UserCheck, CreditCard, Receipt, History, DollarSign, Repeat } from "lucide-react";
import { toast } from "sonner";

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Subscriptions() {
  var auth = useAuth();
  var token = auth.token;
  var user = auth.user;
  var i18n = useTranslation();
  var t = i18n.t;
  
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var overviewState = useState(null);
  var overview = overviewState[0];
  var setOverview = overviewState[1];
  
  var checkingState = useState(false);
  var checking = checkingState[0];
  var setChecking = checkingState[1];
  
  var reactivateDialogState = useState(false);
  var showReactivateDialog = reactivateDialogState[0];
  var setShowReactivateDialog = reactivateDialogState[1];
  
  var deactivateDialogState = useState(false);
  var showDeactivateDialog = deactivateDialogState[0];
  var setShowDeactivateDialog = deactivateDialogState[1];
  
  var selectedUserState = useState(null);
  var selectedUser = selectedUserState[0];
  var setSelectedUser = selectedUserState[1];
  
  var extendDaysState = useState(30);
  var extendDays = extendDaysState[0];
  var setExtendDays = extendDaysState[1];
  
  var reasonState = useState("");
  var deactivateReason = reasonState[0];
  var setDeactivateReason = reasonState[1];
  
  var processingState = useState(false);
  var processing = processingState[0];
  var setProcessing = processingState[1];
  
  // Payment History State
  var [paymentHistory, setPaymentHistory] = useState({ transactions: [], summary: {} });
  var [loadingPayments, setLoadingPayments] = useState(false);
  var [paymentFilter, setPaymentFilter] = useState({ status: "all", method: "all" });
  
  // PayPal Subscriptions State
  var [paypalSubscriptions, setPaypalSubscriptions] = useState({ subscriptions: [], status_counts: {} });
  var [loadingPaypal, setLoadingPaypal] = useState(false);
  
  // All Subscriptions State
  var [allSubscriptions, setAllSubscriptions] = useState({ subscriptions: [], status_counts: {} });
  var [loadingAllSubs, setLoadingAllSubs] = useState(false);
  var [subsFilter, setSubsFilter] = useState("all");
  
  // Active Tab
  var [activeTab, setActiveTab] = useState("overview");

  useEffect(function() {
    fetchOverview();
  }, []);
  
  useEffect(function() {
    if (activeTab === "payments") {
      fetchPaymentHistory();
    } else if (activeTab === "paypal") {
      fetchPaypalSubscriptions();
    } else if (activeTab === "all") {
      fetchAllSubscriptions();
    }
  }, [activeTab]);
  
  // Re-fetch when payment filter changes
  useEffect(function() {
    if (activeTab === "payments") {
      fetchPaymentHistory();
    }
  }, [paymentFilter]);
  
  // Re-fetch when subscription filter changes
  useEffect(function() {
    if (activeTab === "all") {
      fetchAllSubscriptions();
    }
  }, [subsFilter]);

  if (user && user.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t("subscriptions.noPermission")}</AlertDescription>
        </Alert>
      </div>
    );
  }

  function fetchOverview() {
    setLoading(true);
    fetch(API_URL + "/api/admin/subscriptions", {
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(response) {
      if (response.ok) {
        return response.json();
      }
      throw new Error("Failed");
    })
    .then(function(data) {
      setOverview(data);
      setLoading(false);
    })
    .catch(function(err) {
      console.error(err);
      toast.error(t("subscriptions.fetchError"));
      setLoading(false);
    });
  }
  
  function fetchPaymentHistory() {
    setLoadingPayments(true);
    var params = new URLSearchParams();
    if (paymentFilter.status !== "all") params.append("status", paymentFilter.status);
    if (paymentFilter.method !== "all") params.append("method", paymentFilter.method);
    
    fetch(API_URL + "/api/admin/payment-history?" + params.toString(), {
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(response) {
      if (response.ok) return response.json();
      throw new Error("Failed");
    })
    .then(function(data) {
      setPaymentHistory(data);
      setLoadingPayments(false);
    })
    .catch(function(err) {
      console.error(err);
      toast.error(t("subscriptions.fetchError"));
      setLoadingPayments(false);
    });
  }
  
  function fetchPaypalSubscriptions() {
    setLoadingPaypal(true);
    fetch(API_URL + "/api/admin/paypal-subscriptions", {
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(response) {
      if (response.ok) return response.json();
      throw new Error("Failed");
    })
    .then(function(data) {
      setPaypalSubscriptions(data);
      setLoadingPaypal(false);
    })
    .catch(function(err) {
      console.error(err);
      setLoadingPaypal(false);
    });
  }
  
  function fetchAllSubscriptions() {
    setLoadingAllSubs(true);
    var params = new URLSearchParams();
    if (subsFilter !== "all") params.append("status", subsFilter);
    
    fetch(API_URL + "/api/admin/all-subscriptions?" + params.toString(), {
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(response) {
      if (response.ok) return response.json();
      throw new Error("Failed");
    })
    .then(function(data) {
      setAllSubscriptions(data);
      setLoadingAllSubs(false);
    })
    .catch(function(err) {
      console.error(err);
      setLoadingAllSubs(false);
    });
  }

  function handleCheckNow() {
    setChecking(true);
    fetch(API_URL + "/api/admin/subscriptions/check-now", {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(response) {
      if (response.ok) {
        return response.json();
      }
      throw new Error("Failed");
    })
    .then(function(result) {
      toast.success(t("subscriptions.checkComplete", { 
        deactivated: result.deactivated.length,
        expired: result.trials_expired.length 
      }));
      fetchOverview();
      setChecking(false);
    })
    .catch(function(err) {
      console.error(err);
      toast.error(t("subscriptions.checkError"));
      setChecking(false);
    });
  }

  function handleReactivate() {
    if (!selectedUser) return;
    
    setProcessing(true);
    fetch(API_URL + "/api/admin/subscriptions/" + selectedUser.user_id + "/reactivate", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? "Bearer " + token : ""
      },
      body: JSON.stringify({ extend_days: extendDays })
    })
    .then(function(response) {
      if (response.ok) {
        toast.success(t("subscriptions.reactivateSuccess"));
        setShowReactivateDialog(false);
        fetchOverview();
      } else {
        toast.error(t("subscriptions.reactivateError"));
      }
      setProcessing(false);
    })
    .catch(function(err) {
      console.error(err);
      toast.error(t("subscriptions.reactivateError"));
      setProcessing(false);
    });
  }

  function handleDeactivate() {
    if (!selectedUser) return;
    
    setProcessing(true);
    fetch(API_URL + "/api/admin/subscriptions/" + selectedUser.user_id + "/deactivate", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? "Bearer " + token : ""
      },
      body: JSON.stringify({ reason: deactivateReason || "manual_deactivation" })
    })
    .then(function(response) {
      if (response.ok) {
        toast.success(t("subscriptions.deactivateSuccess"));
        setShowDeactivateDialog(false);
        fetchOverview();
      } else {
        toast.error(t("subscriptions.deactivateError"));
      }
      setProcessing(false);
    })
    .catch(function(err) {
      console.error(err);
      toast.error(t("subscriptions.deactivateError"));
      setProcessing(false);
    });
  }

  function openReactivateDialog(userItem) {
    setSelectedUser(userItem);
    setExtendDays(30);
    setShowReactivateDialog(true);
  }

  function openDeactivateDialog(userItem) {
    setSelectedUser(userItem);
    setDeactivateReason("");
    setShowDeactivateDialog(true);
  }

  function formatDate(dateStr) {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  var statusCounts = overview ? overview.status_counts : {};
  var overdueUsers = overview ? overview.overdue_users : [];
  var recentlyDeactivated = overview ? overview.recently_deactivated : [];
  var upcomingPayments = overview ? overview.upcoming_payments : [];

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("subscriptions.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("subscriptions.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={function() { fetchOverview(); if (activeTab === "payments") fetchPaymentHistory(); if (activeTab === "paypal") fetchPaypalSubscriptions(); if (activeTab === "all") fetchAllSubscriptions(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            {t("common.refresh")}
          </Button>
          <Button onClick={handleCheckNow} disabled={checking} data-testid="check-now-btn">
            {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {checking ? t("subscriptions.checking") : t("subscriptions.checkNow")}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {t("subscriptions.tabs.overview")}
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            {t("subscriptions.tabs.payments")}
          </TabsTrigger>
          <TabsTrigger value="paypal" className="flex items-center gap-2">
            <Repeat className="w-4 h-4" />
            {t("subscriptions.tabs.recurring")}
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            {t("subscriptions.tabs.all")}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="active-count-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {t("subscriptions.status.active")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{statusCounts.active || 0}</div>
              </CardContent>
            </Card>
            <Card data-testid="trial-count-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  {t("subscriptions.status.trial")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{statusCounts.trial || 0}</div>
              </CardContent>
            </Card>
            <Card data-testid="expired-count-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  {t("subscriptions.status.expired")}
                </CardTitle>
              </CardHeader>
              <CardContent>
            <div className="text-3xl font-bold text-orange-600">{statusCounts.expired || 0}</div>
          </CardContent>
        </Card>
        <Card data-testid="deactivated-count-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              {t("subscriptions.status.deactivated")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{statusCounts.deactivated || 0}</div>
          </CardContent>
        </Card>
      </div>

      {overdueUsers.length > 0 && (
        <Card className="border-orange-500/50" data-testid="overdue-users-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              {t("subscriptions.overdueUsers")} ({overdueUsers.length})
            </CardTitle>
            <CardDescription>{t("subscriptions.overdueDescription", { days: 7 })}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {overdueUsers.map(function(item, idx) {
                return (
                  <div key={item.user_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium">{item.email}</p>
                      <p className="text-sm text-muted-foreground">{t("subscriptions.paymentDue")}: {formatDate(item.next_payment_date)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={openReactivateDialog.bind(null, JSON.parse(JSON.stringify(item)))}>
                        <UserCheck className="w-3 h-3 mr-1" />{t("subscriptions.reactivate")}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={openDeactivateDialog.bind(null, JSON.parse(JSON.stringify(item)))}>
                        <UserX className="w-3 h-3 mr-1" />{t("subscriptions.deactivate")}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {upcomingPayments.length > 0 && (
        <Card data-testid="upcoming-payments-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" />
              {t("subscriptions.upcomingPayments")} ({upcomingPayments.length})
            </CardTitle>
            <CardDescription>{t("subscriptions.upcomingDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {upcomingPayments.map(function(item, idx) {
                return (
                  <div key={item.user_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium">{item.email}</p>
                      <p className="text-sm text-muted-foreground">{item.name}</p>
                    </div>
                    <Badge variant="outline">{formatDate(item.next_payment_date)}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {recentlyDeactivated.length > 0 && (
        <Card data-testid="recently-deactivated-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <UserX className="w-5 h-5" />
              {t("subscriptions.recentlyDeactivated")} ({recentlyDeactivated.length})
            </CardTitle>
            <CardDescription>{t("subscriptions.deactivatedDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentlyDeactivated.map(function(item, idx) {
                return (
                  <div key={item.user_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium">{item.email}</p>
                      <p className="text-sm text-muted-foreground">{t("subscriptions.reason")}: {item.deactivation_reason || "unknown"}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={openReactivateDialog.bind(null, JSON.parse(JSON.stringify(item)))}>
                      <UserCheck className="w-3 h-3 mr-1" />{t("subscriptions.reactivate")}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="payments" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {t("subscriptions.paymentHistory")}
            </h2>
            <div className="flex gap-2">
              <Select value={paymentFilter.status} onValueChange={function(v) { setPaymentFilter({...paymentFilter, status: v}); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("subscriptions.allStatuses")}</SelectItem>
                  <SelectItem value="paid">{t("subscriptions.status.paid")}</SelectItem>
                  <SelectItem value="pending">{t("subscriptions.status.pending")}</SelectItem>
                  <SelectItem value="failed">{t("subscriptions.status.failed")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={paymentFilter.method} onValueChange={function(v) { setPaymentFilter({...paymentFilter, method: v}); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("subscriptions.allMethods")}</SelectItem>
                  <SelectItem value="stripe">{t("subscriptions.stripe")}</SelectItem>
                  <SelectItem value="paypal">{t("subscriptions.paypal")}</SelectItem>
                  <SelectItem value="paypal_recurring">{t("subscriptions.paypalRecurring")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchPaymentHistory}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Revenue Summary */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  {t("subscriptions.totalRevenue")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  €{(paymentHistory.summary?.total_revenue || 0).toFixed(2)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  {t("subscriptions.stripePayments")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {paymentHistory.summary?.method_breakdown?.stripe?.count || 0}
                </div>
                <p className="text-sm text-muted-foreground">
                  €{(paymentHistory.summary?.method_breakdown?.stripe?.amount || 0).toFixed(2)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-yellow-500" />
                  {t("subscriptions.paypalPayments")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(paymentHistory.summary?.method_breakdown?.paypal?.count || 0) + (paymentHistory.summary?.method_breakdown?.paypal_recurring?.count || 0)}
                </div>
                <p className="text-sm text-muted-foreground">
                  €{((paymentHistory.summary?.method_breakdown?.paypal?.amount || 0) + (paymentHistory.summary?.method_breakdown?.paypal_recurring?.amount || 0)).toFixed(2)}
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Transactions Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                {t("subscriptions.transactions")}
              </CardTitle>
              <CardDescription>{t("subscriptions.transactionsDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPayments ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : paymentHistory.transactions?.length > 0 ? (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {paymentHistory.transactions.map(function(txn, idx) {
                    return (
                      <div key={txn.transaction_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{txn.user_email || txn.user_id}</p>
                            <Badge variant={txn.payment_status === "paid" ? "default" : txn.payment_status === "pending" ? "secondary" : "destructive"}>
                              {txn.payment_status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {txn.payment_method} • {formatDate(txn.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{txn.currency} {(txn.amount || 0).toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">{txn.transaction_id}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">{t("subscriptions.noTransactions")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PayPal Recurring Subscriptions Tab */}
        <TabsContent value="paypal" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Repeat className="w-5 h-5" />
              {t("subscriptions.recurringSubscriptions")}
            </h2>
            <Button variant="outline" onClick={fetchPaypalSubscriptions}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("common.refresh")}
            </Button>
          </div>
          
          {/* Status Summary */}
          <div className="grid sm:grid-cols-4 gap-4">
            {Object.entries(paypalSubscriptions.status_counts || {}).map(function([status, count]) {
              var statusColor = status === "ACTIVE" ? "text-green-600" : status === "CANCELLED" ? "text-red-600" : status === "SUSPENDED" ? "text-orange-600" : "text-gray-600";
              return (
                <Card key={status}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground capitalize">{status.toLowerCase()}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={"text-2xl font-bold " + statusColor}>{count}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Subscriptions List */}
          <Card>
            <CardHeader>
              <CardTitle>{t("subscriptions.paypalSubscriptionsList")}</CardTitle>
              <CardDescription>{t("subscriptions.paypalSubscriptionsListDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPaypal ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : paypalSubscriptions.subscriptions?.length > 0 ? (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {paypalSubscriptions.subscriptions.map(function(sub, idx) {
                    return (
                      <div key={sub.subscription_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{sub.user_email || sub.user_id}</p>
                            <Badge variant={sub.status === "ACTIVE" ? "default" : sub.status === "CANCELLED" ? "destructive" : "secondary"}>
                              {sub.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t("subscriptions.created")}: {formatDate(sub.created_at)}
                            {sub.next_billing_time && " • " + t("subscriptions.nextBilling") + ": " + formatDate(sub.next_billing_time)}
                          </p>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          <p>{sub.paypal_subscription_id}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">{t("subscriptions.noPaypalSubscriptions")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Subscriptions Tab */}
        <TabsContent value="all" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t("subscriptions.allSubscriptions")}
            </h2>
            <div className="flex gap-2">
              <Select value={subsFilter} onValueChange={setSubsFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("subscriptions.allStatuses")}</SelectItem>
                  <SelectItem value="active">{t("subscriptions.status.active")}</SelectItem>
                  <SelectItem value="trial">{t("subscriptions.status.trial")}</SelectItem>
                  <SelectItem value="expired">{t("subscriptions.status.expired")}</SelectItem>
                  <SelectItem value="deactivated">{t("subscriptions.status.deactivated")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchAllSubscriptions}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Status Summary */}
          <div className="grid sm:grid-cols-4 gap-4">
            {Object.entries(allSubscriptions.status_counts || {}).map(function([status, count]) {
              var statusColor = status === "active" ? "text-green-600" : status === "trial" ? "text-blue-600" : status === "expired" ? "text-orange-600" : "text-red-600";
              return (
                <Card key={status}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground capitalize">{status}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={"text-2xl font-bold " + statusColor}>{count}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* All Users List */}
          <Card>
            <CardHeader>
              <CardTitle>{t("subscriptions.managersSubscriptions")}</CardTitle>
              <CardDescription>{t("subscriptions.managersSubscriptionsDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAllSubs ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : allSubscriptions.subscriptions?.length > 0 ? (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {allSubscriptions.subscriptions.map(function(user, idx) {
                    var statusBadge = user.subscription_status === "active" ? "default" : user.subscription_status === "trial" ? "secondary" : "destructive";
                    return (
                      <div key={user.user_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{user.email}</p>
                            <Badge variant={statusBadge}>{user.subscription_status}</Badge>
                            {user.subscription_type === "recurring" && (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                <Repeat className="w-3 h-3 mr-1" />{t("subscriptions.recurring")}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {user.name} • {user.role}
                            {user.payment_count > 0 && " • " + user.payment_count + " " + t("subscriptions.payments")}
                          </p>
                        </div>
                        <div className="text-right text-sm">
                          {user.next_payment_date && (
                            <p className="text-muted-foreground">{t("subscriptions.nextPayment")}: {formatDate(user.next_payment_date)}</p>
                          )}
                          <div className="flex gap-1 mt-1">
                            <Button size="sm" variant="outline" onClick={openReactivateDialog.bind(null, JSON.parse(JSON.stringify(user)))}>
                              <UserCheck className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-destructive" onClick={openDeactivateDialog.bind(null, JSON.parse(JSON.stringify(user)))}>
                              <UserX className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">{t("subscriptions.noSubscriptions")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showReactivateDialog} onOpenChange={setShowReactivateDialog}>
        <DialogContent data-testid="reactivate-dialog">
          <DialogHeader>
            <DialogTitle>{t("subscriptions.reactivateUser")}</DialogTitle>
            <DialogDescription>
              {t("subscriptions.reactivateDescription", { email: selectedUser ? selectedUser.email : "" })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("subscriptions.extendDays")}</Label>
              <Input 
                type="number" 
                value={extendDays} 
                onChange={function(e) { setExtendDays(parseInt(e.target.value) || 30); }}
                min={1}
                max={365}
                data-testid="extend-days-input"
              />
              <p className="text-xs text-muted-foreground">{t("subscriptions.extendDaysHelp")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowReactivateDialog(false); }}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleReactivate} disabled={processing} data-testid="confirm-reactivate-btn">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
              {t("subscriptions.reactivate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent data-testid="deactivate-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">{t("subscriptions.deactivateUser")}</DialogTitle>
            <DialogDescription>
              {t("subscriptions.deactivateWarning", { email: selectedUser ? selectedUser.email : "" })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("subscriptions.reason")}</Label>
              <Input 
                value={deactivateReason} 
                onChange={function(e) { setDeactivateReason(e.target.value); }}
                placeholder={t("subscriptions.reasonPlaceholder")}
                data-testid="deactivate-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowDeactivateDialog(false); }}>
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleDeactivate} disabled={processing} data-testid="confirm-deactivate-btn">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserX className="w-4 h-4 mr-2" />}
              {t("subscriptions.deactivate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
